import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useCreateMutation, useShowQuery } from "../../api/genericHooks";
import { LanguageSelectorModalButton } from "../../components/Buttons/LanguageSelectorModalButton";
import { BookContext } from "../../contexts/BookContext";
import { logo } from "../../images";
import { reset, setUser, signOut } from "../../store/slices/auth";
import { dispatch } from "../../store/store";

type LoginData = {
  email: string;
  password: string;
};

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  let refreshCounter = 0;
  const bookContext = useContext(BookContext);
  const [userId, setUserId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onLogout = async () => {
    bookContext.clear();
    dispatch(signOut());
  };

  useEffect(() => {
    if (state?.logout === true && refreshCounter === 0) {
      onLogout().then(() => {
        dispatch(reset());
      });
    }
    refreshCounter++;
  }, [state?.logout]);

  //Query Methods
  const loginMutation = useCreateMutation("/login");
  useShowQuery("/users", userId, {
    enabled: !!userId,
    onSuccess: (response) => {
      dispatch(setUser({ user: response }));
      navigate("/dashboard");
    },
  });

  // Form Methods
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();

  const onSubmit: SubmitHandler<LoginData> = (data) => {
    loginMutation
      .mutateAsync({
        user: {
          email: data.email,
          password: data.password,
        },
      })
      .then((response) => {
        if (response.status.code === 200) {
          setUserId(response.user.id);
        }
      });
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-delicate-50">
        <div className="w-full justify-items-end bg-delicate-50 sm:block">
          <LanguageSelectorModalButton />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="Memoirs Logo"
            src={logo.logoGrey}
            className="mx-auto h-20 w-auto"
          />
          <h2 className="text-center text-2xl/9 font-poppins font-bold tracking-tight text-slate-100">
            {t("login.connexion")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm/6 font-nunito font-medium text-slate-900"
                >
                  {t("login.fields.email")}
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("email", { required: true })}
                  />
                  {errors.email && (
                    <span className="text-rust-50">{t("errors.required")}</span>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm/6 font-nunito font-medium text-slate-900"
                >
                  {t("login.fields.password")}
                </label>

                <div className="mt-2 flex">
                  <div className="-mr-px grid grow grid-cols-1 focus-within:relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="block w-full font-nunitobg-white px-3 py-1.5 rounded-l-md text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                      {...register("password", { required: true })}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="flex shrink-0 items-center gap-x-1.5 rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 focus:relative focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-600"
                  >
                    {showPassword ? (
                      <EyeSlashIcon
                        aria-hidden="true"
                        className="-ml-0.5 size-4 text-gray-400"
                      />
                    ) : (
                      <EyeIcon
                        aria-hidden="true"
                        className="-ml-0.5 size-4 text-gray-400"
                      />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-rust-50">{t("errors.required")}</span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm/6">
                  <NavLink
                    to="/reset-password"
                    className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
                  >
                    {t("signup.forgotPassword")}
                  </NavLink>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="flex w-full font-nunito justify-center rounded bg-rust-400 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-rust-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust-400"
                >
                  {t("login.login")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
